import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import RedirectionTooLongImage from 'src/images/other/redirection-too-long.gif';
import PopupBlocked from 'src/images/other/popups-blocked.webp';
import { useOpenItemsInNewTab, useReceiveLogic } from 'src/hooks';
import { ReceiveModalFlow } from './ReceiveModalFlow';
import type { ReceiveModalFlowStep } from 'src/types';

type Props = {
  cartId: string;
};

export const RedirectionTakingTooLongModal = ({ cartId }: Props) => {
  const { t } = useTranslation();
  const nav = useNavigate();

  const { trySendingItems, tooLong, setTooLong } = useReceiveLogic();

  const openItemsInNewTab = useOpenItemsInNewTab({
    page: 'Receive',
    modal: 'Redirection Taking Too Long Modal',
  });

  const steps: ReceiveModalFlowStep[] = [
    {
      mainImage: RedirectionTooLongImage,
      title: t('popup.receive.lets_do_it_the_old_fashioned_way'),
      description: t(
        'popup.receive.there_was_an_unknown_error_loading_this_cart',
      ),
      primaryButtonText: t('popup.receive.load_cart_manually'),
      // If the receive times out, we try sending the cart to the vendor using the same logic as the Get page.
      // If the cart is sent successfully, the page will be redirected to the Get page since we cannot close the window.
      // If the cart is not sent successfully, we will continue through the modal flow.
      primaryButtonFunction: async () => {
        const result = await trySendingItems();

        if (!result) {
          return;
        }

        const navUrl = `/get/${cartId}${result === 'receive_chunk' ? '?show_chunk=true' : ''}`;
        nav(navUrl);
      },
      goToStepAfterPrimary: 1,
    },
    {
      animationStep: 1,
      title: t('popup.receive.split_cart_items_up'),
      description: t('popup.receive.first_we_need_to_split_the_cart_up'),
      primaryButtonText: t('popup.receive.split_cart_up'),
      primaryButtonFunction: openItemsInNewTab,
      goToStepAfterPrimary: 2,
    },
    {
      animationStep: 2,
      title: t('popup.receive.check_items_were_opened_as_pages'),
      description: t('popup.receive.did_your_browser_just_open_each_item'),
      primaryButtonText: t('popup.receive.yes_theyre_all_open'),
      goToStepAfterPrimary: 4,
      secondaryButtonText: t('popup.receive.nope_cant_see_them_all'),
      goToStepAfterSecondary: 3,
    },
    {
      animationStep: 3,
      title: t('popup.receive.you_may_need_to_allow_browser_pop_ups'),
      description: t('popup.receive.click_the_browsers_pops_blocked_icon'),
      primaryButtonText: t('popup.receive.ok_ive_done_this'),
      secondaryImage: PopupBlocked,
      goToStepAfterPrimary: 1,
    },
    {
      animationStep: 4,
      title: t('popup.receive.add_items_to_cart_manually'),
      description: t(
        'popup.receive.now_you_just_need_to_add_each_item_on_your_own',
      ),
      primaryButtonText: t('popup.receive.got_it_will_do'),
      primaryButtonFunction: () => {
        setTooLong(false);
      },
      afterButtonsBlock: (
        <p className='text-100 text-pretty text-center'>
          {t('popup.receive.ps_this_manual_process_is_a_very_rare_occurrence')}
        </p>
      ),
    },
  ];

  return (
    <ReceiveModalFlow
      open={tooLong}
      onClose={() => setTooLong(false)}
      steps={steps}
      customMaxWidth={582}
    />
  );
};
